import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { getReferrerId, getUtmParams } from 'hooks/useUrlParams'
import Router from 'next/router'
import { amplitudePrograms, trackAmplitude } from 'services/amplitude/amplitude'
import { trackLead } from 'services/engagementTracking'
import { createFbEventId, getFbcFbpValues } from 'services/engagementTracking/facebookPixel'
import esAPI from 'services/esAPI/esAPI'
import { beginSession } from 'services/session/session'
import { JUMPSTART } from '@containers/PurchaseApi/PurchaseContainer'

const splitFullName = (fullName = '') => {
  const parts = fullName.trim().split(/\s+/)
  return [parts[0] || '', parts.slice(1).join(' ')]
}

// Digits only from react-phone-input-2; the API validates properly
const guardPhone = (phone) => {
  if (phone && !/^\d{10,15}$/.test(phone)) throw new Error('Please enter a valid phone number.')
}

/**
 * Intake modal → passwordless account via lead-capture → checkout hand-off.
 * Returns `false` so the modal skips its confirmation while redirecting.
 * `jumpstartFocus` comes from the focus pages only and is recorded on the member for the tailored programme.
 */
export const useJumpstartIntake = (source, { jumpstartFocus } = {}) =>
  useCallback(
    async (values) => {
      const [firstName, lastName] = splitFullName(values.fullName)
      if (!lastName) throw new Error('Please enter your first and last name.')
      if (!values.acceptedTerms) throw new Error('Please accept the Terms of Use to continue.')
      guardPhone(values.phone)

      const eventId = createFbEventId()

      await esAPI('post', `/lead-capture/${source}`, {
        firstName,
        lastName,
        email: values.email,
        phone: values.phone,
        // experience and stage now come from the day-one assessment
        signupCountry: values.countryCode,
        acceptedTerms: true,
        ...(jumpstartFocus ? { jumpstartFocus } : {}),
        eventId,
        ...getFbcFbpValues(),
        ...getUtmParams(),
        // from the referral email's link, so the referrer is credited even if a different email is used
        ...(getReferrerId() ? { referrerId: Number(getReferrerId()) } : {}),
      })

      trackLead(source, eventId, { email: values.email, phone: values.phone })

      // An existing account gets no session from a bare email (account
      // takeover risk) — it gets an emailed sign-in link instead
      const signedIn = await esAPI('get', '/auth/me')
        .then((result) => !!(result?.user?.id || result?.id))
        .catch(() => false)

      if (signedIn) {
        // A session here means the account was just created for this intake
        trackAmplitude('sign_up_completed', {
          ...amplitudePrograms[JUMPSTART],
          source,
          ...(jumpstartFocus ? { focus: jumpstartFocus } : {}),
        })
        await Router.push(`/payment/${JUMPSTART}`)
        return false
      }

      // request-magic-sign-in emails a direct sign-in link whether or not the
      // account has a password; request-create-password would send password
      // holders a reset email instead of the promised sign-in link
      await esAPI('post', '/auth/request-magic-sign-in', { email: values.email }).catch(() => {})

      return {
        successTitle: 'Welcome back!',
        successBody:
          "You already have an account with this email. We've sent you a secure sign-in link — open it to continue to your Jumpstart checkout.",
      }
    },
    [source, jumpstartFocus],
  )

/**
 * Report request → lead tagged with the report slug; Brevo sends the PDF via
 * the template mapped to that slug (no email until the ID is configured).
 *
 * A new lead is also signed in, without leaving the page: /course then sends an
 * account with no programme to the Jumpstart checkout. An existing account only
 * gets the report: the sign-in link it was emailed alongside came off on
 * 3 November 2026, since two emails for one download read as a mistake.
 */
export const useReportRequest = () => {
  const queryClient = useQueryClient()

  return useCallback(
    async (values, report) => {
      const [firstName, lastName] = splitFullName(values.fullName)
      if (!lastName) throw new Error('Please enter your first and last name.')
      guardPhone(values.phone)

      const eventId = createFbEventId()

      await esAPI('post', `/lead-capture/${report.slug}`, {
        firstName,
        lastName,
        email: values.email,
        phone: values.phone,
        signupCountry: values.countryCode,
        experienceLevel: values.experience,
        currentStage: values.stage,
        eventId,
        ...getFbcFbpValues(),
        ...getUtmParams(),
      })

      trackLead(report.slug, eventId, { email: values.email, phone: values.phone })

      // A new account gets a session; an existing one stays signed out (a bare email is a takeover risk)
      const me = await esAPI('get', '/auth/me').catch(() => null)
      if (me?.user?.id) beginSession(queryClient, me.user)
      // restore to email an existing account a sign-in link with its report:
      // else await esAPI('post', '/auth/request-magic-sign-in', { email: values.email }).catch(() => {})
      // and return { successBody: '… along with a secure sign-in link for your existing account …' }
      return undefined
    },
    [queryClient],
  )
}

/** Waitlist submission: the six intake fields as a lead, tagged per programme */
export const useWaitlistIntake = (source) =>
  useCallback(
    async (values) => {
      const [firstName, lastName] = splitFullName(values.fullName)
      if (!lastName) throw new Error('Please enter your first and last name.')
      guardPhone(values.phone)

      const eventId = createFbEventId()

      await esAPI('post', `/lead-capture/${source}`, {
        firstName,
        lastName,
        email: values.email,
        phone: values.phone,
        signupCountry: values.countryCode,
        experienceLevel: values.experience,
        currentStage: values.stage,
        // recorded on the lead and in Brevo so campaigns know which programme they asked about
        programInterest: source === 'accelerate-waitlist' ? 'Accelerate' : 'Full Certification',
        eventId,
        ...getFbcFbpValues(),
        ...getUtmParams(),
      })

      trackLead(source, eventId, { email: values.email, phone: values.phone })

      trackAmplitude('program_enrollment_started', {
        ...(source === 'accelerate-waitlist'
          ? { program_id: 'accelerate', program_name: 'Accelerate' }
          : { program_id: 'full_certification', program_name: 'Full Certification' }),
        method: 'waitlist',
      })
    },
    [source],
  )

export default useJumpstartIntake
